import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Winner from "./winner.js";
import OddsTable from "../OddsTable.js";
import Table from "react-bootstrap/Table";
import { styles } from "../../styles/styles.js";
import { StarRating } from "../../components/starrating.js";
import createScrollSnap from "scroll-snap";
import Card from "react-bootstrap/Card";
import * as motion from "motion/react-client";
import ReactMarkdown from "react-markdown";
import { useAppState } from "../../AppStateContext.js";
import { Oval } from "react-loading-icons";
import HorizontalBarChart from "./barchart.js";
import { LastTen } from "./lastten.js";
import { H2HHistoryNote } from "./historynote.js";
import { Chart } from "./pointdiff.js";
import { fmt, sum, diff, mean, rate, rateValue, record, winRate, winRateValue, stars, isNum, EM_DASH } from "../../utils/format.js";

// A two-part cell ("24 - 19") whose separator is itself a dash cannot show
// per-half absence markers legibly, so an unknown half makes the whole cell
// unknown. These used to render "null - null" and "undefined - NaN".
const pair = (left, right) =>
  isNum(left) && isNum(right) ? `${left} - ${right}` : EM_DASH;

export function HeadtoHead({ team1, team2, game, onClose }) {
  const [data, setData] = useState([]);
  const [displayMode, setDisplayMode] = useState(2); // 0: Win-Loss, 1: Percentages, 2: Stars
  const [showPointDifference, setShowPointDifference] = useState(true);
  const containerRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDesktopMode } = useAppState();

  useEffect(() => {
    if (isDesktopMode) return;
    const container = containerRef.current;
    if (container) {
      const { bind, unbind } = createScrollSnap(
        container,
        {
          snapDestinationY: "0%",
          timeout: 100,
          duration: 300,
          threshold: 0.2,
          snapStop: false,
          easing: (t) => t * (2 - t),
        },
        () => console.log("Element snapped")
      );

      bind();
      return () => unbind();
    }
  }, [isDesktopMode]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const url = process.env.REACT_APP_API_URL;
      const { data: response } = await axios.get(`${url}/getFixtures/`, {
        params: {
          team1: team1,
          team2: team2,
          date: game.game_time,
          league: game.league,
        },
      });
      setData(response);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDisplayMode = () => {
    setDisplayMode((prevMode) => (prevMode + 1) % 3);
  };

  const togglePointDifference = () => {
    setShowPointDifference(!showPointDifference);
  };

  // Used to display draws/ties
  function isSupportedSport(fixture) {
    const supportedSports = ["Hockey", "Soccer", "RugbyLeague", "AussieRules"];
    return supportedSports.includes(game.sport);
  }

  const AnimatedCell = ({ children }) => (
    <motion.div
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 1,
        scale: { type: "spring", damping: 10, stiffness: 50, bounce: 0.5 },
      }}>
      {children}
    </motion.div>
  );

  // This component never fetches head_to_head — it arrives on the fixture prop.
  // So "Loading..." here was unreachable-by-resolution: a fixture without the
  // blob showed a spinner message forever. It is absent data, and says so.
  if (!game.head_to_head) {
    return (
      <div style={{ ...styles.unknown, padding: '20px' }}>
        No head-to-head history for these teams.
      </div>
    );
  }

  const containerStyle = isDesktopMode
    ? { overflowY: "visible", height: "auto" }
    : { overflowY: "scroll", height: "100vh", scrollSnapType: "y mandatory" };

  const sectionSnap = isDesktopMode
    ? { ...styles.sectionStyleDesktop }
    : { ...styles.sectionStyle, minHeight: "150vh", scrollSnapAlign: "start" };

  return (
    <div
      ref={containerRef}
      style={containerStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}>
        <section style={sectionSnap}>
          {displayMode !== 2 && (
            <Table bordered className="main">
              <tbody>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Wins
                  </td>
                  <td className="Cells" style={styles.column_1}></td>
                  <td className="Cells" style={styles.column_1}></td>
                </tr>
                <tr>
                  <td className="Cells" style={styles.column_1}></td>
                  <td className="Cells" style={{ ...styles.column_1, color: "#20B2AA" }}>
                    {game.home_team}
                  </td>
                  <td className="Cells" style={{ ...styles.column_1, color: "red" }}>
                    {game.away_team}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 3
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_home_win_3} - ${game.head_to_head.h2h_home_loss_3}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_3}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_home_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_home_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3),
                            winRateValue(game.head_to_head.h2h_away_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_away_win_3} - ${game.head_to_head.h2h_away_loss_3}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_3}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_away_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_away_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3),
                            winRateValue(game.head_to_head.h2h_home_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 5
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_home_win_5} - ${game.head_to_head.h2h_home_loss_5}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_5}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_home_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_home_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5),
                            winRateValue(game.head_to_head.h2h_away_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_away_win_5} - ${game.head_to_head.h2h_away_loss_5}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_5}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_away_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_away_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5),
                            winRateValue(game.head_to_head.h2h_home_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 10
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_home_win_10} - ${game.head_to_head.h2h_home_loss_10}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_10}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_home_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_home_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10),
                            winRateValue(game.head_to_head.h2h_away_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        `${game.head_to_head.h2h_away_win_10} - ${game.head_to_head.h2h_away_loss_10}${
                          isSupportedSport(game) ? ` - ${game.head_to_head.h2h_draws_10}` : ""
                        }`
                      ) : displayMode === 1 ? (
                        winRate(game.head_to_head.h2h_away_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10)
                      ) : (
                        <StarRating
                          rating={stars(
                            winRateValue(game.head_to_head.h2h_away_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10),
                            winRateValue(game.head_to_head.h2h_home_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
              </tbody>
            </Table>
          )}

          {displayMode === 2 && (
            <div
              style={{
                width: "100%",
                marginTop: "0px",
                alignSelf: "flex-start",
              }}>
              <Table bordered className="main" onClick={toggleDisplayMode}>
                <tbody>
                  <tr style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      Wins
                    </td>
                    <td className="Cells" style={styles.Cells}></td>
                    <td className="Cells" style={styles.Cells}></td>
                  </tr>
                  <tr>
                    <td className="Cells" style={{ ...styles.column_1, width: "5%" }}></td>
                    <td className="Cells" style={{ ...styles.column_1, width: "25%", color: "#20B2AA" }}>
                      {game.home_team}
                    </td>
                    <td className="Cells" style={{ ...styles.column_1, width: "25%", color: "red" }}>
                      {game.away_team}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <HorizontalBarChart
                shortNames={["3G", "5G", "10G"]}
                rawData={[
                  diff(winRateValue(game.head_to_head.h2h_away_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3), winRateValue(game.head_to_head.h2h_home_win_3, game.head_to_head.h2h_draws_3, game.head_to_head.h2h_games_sample_3, 3)),
                  diff(winRateValue(game.head_to_head.h2h_away_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5), winRateValue(game.head_to_head.h2h_home_win_5, game.head_to_head.h2h_draws_5, game.head_to_head.h2h_games_sample_5, 5)),
                  diff(winRateValue(game.head_to_head.h2h_away_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10), winRateValue(game.head_to_head.h2h_home_win_10, game.head_to_head.h2h_draws_10, game.head_to_head.h2h_games_sample_10, 10)),
                ]}
                names={["Last 3", "Last 5", "Last 10"]}
                onClick={toggleDisplayMode}
              />
            </div>
          )}

          <H2HHistoryNote game={game} />
          <div style={{ height: "2vh" }}></div>
          <LastTen
            results={Array(10)
              .fill()
              .map((_, i) => game.head_to_head[`h2h_${i + 1}_winner`] == game.home_team)
              .reverse()}
            title={game.home_team + " vs " + game.away_team + " wins"}
            mode="h2h"
          />
          <div style={{ height: "5vh" }}></div>

          {
            <ul
              style={{
                listStyleType: "none",
                padding: 0,
                margin: 0,
                fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
              }}>
              <ReactMarkdown>{game.box_score && game.box_score["wins_h2h"]}</ReactMarkdown>
            </ul>
          }
        </section>

        <section style={sectionSnap}>
          {displayMode !== 2 && (
            <Table bordered className="main">
              <tbody>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1}>
                    Spreads
                  </td>
                  <td className="Cells" style={styles.column_1}></td>
                  <td className="Cells" style={styles.column_1}></td>
                </tr>
                <tr>
                  <td className="Cells" style={styles.column_1}></td>
                  <td className="Cells" style={{ ...styles.column_1, color: "#20B2AA" }}>
                    {game.home_team}
                  </td>
                  <td className="Cells" style={{ ...styles.column_1, color: "red" }}>
                    {game.away_team}
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 3
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_home_spread_3, game.head_to_head.h2h_home_spread_sample_3, 3)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_home_spread_3, game.head_to_head.h2h_home_spread_sample_3, 3)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_home_spread_3, game.head_to_head.h2h_home_spread_sample_3, 3),
                            rateValue(game.head_to_head.h2h_away_spread_3, game.head_to_head.h2h_away_spread_sample_3, 3)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_away_spread_3, game.head_to_head.h2h_away_spread_sample_3, 3)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_away_spread_3, game.head_to_head.h2h_away_spread_sample_3, 3)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_away_spread_3, game.head_to_head.h2h_away_spread_sample_3, 3),
                            rateValue(game.head_to_head.h2h_home_spread_3, game.head_to_head.h2h_home_spread_sample_3, 3)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 5
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_home_spread_5, game.head_to_head.h2h_home_spread_sample_5, 5)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_home_spread_5, game.head_to_head.h2h_home_spread_sample_5, 5)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_home_spread_5, game.head_to_head.h2h_home_spread_sample_5, 5),
                            rateValue(game.head_to_head.h2h_away_spread_5, game.head_to_head.h2h_away_spread_sample_5, 5)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_away_spread_5, game.head_to_head.h2h_away_spread_sample_5, 5)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_away_spread_5, game.head_to_head.h2h_away_spread_sample_5, 5)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_away_spread_5, game.head_to_head.h2h_away_spread_sample_5, 5),
                            rateValue(game.head_to_head.h2h_home_spread_5, game.head_to_head.h2h_home_spread_sample_5, 5)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
                <tr style={styles.Cells}>
                  <td className="Cells" style={styles.column_1} onClick={toggleDisplayMode}>
                    Last 10
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_home_spread_10, game.head_to_head.h2h_home_spread_sample_10, 10)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_home_spread_10, game.head_to_head.h2h_home_spread_sample_10, 10)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_home_spread_10, game.head_to_head.h2h_home_spread_sample_10, 10),
                            rateValue(game.head_to_head.h2h_away_spread_10, game.head_to_head.h2h_away_spread_sample_10, 10)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                  <td className="Cells" style={styles.Cells}>
                    <AnimatedCell>
                      {displayMode === 0 ? (
                        record(game.head_to_head.h2h_away_spread_10, game.head_to_head.h2h_away_spread_sample_10, 10)
                      ) : displayMode === 1 ? (
                        rate(game.head_to_head.h2h_away_spread_10, game.head_to_head.h2h_away_spread_sample_10, 10)
                      ) : (
                        <StarRating
                          rating={stars(
                            rateValue(game.head_to_head.h2h_away_spread_10, game.head_to_head.h2h_away_spread_sample_10, 10),
                            rateValue(game.head_to_head.h2h_home_spread_10, game.head_to_head.h2h_home_spread_sample_10, 10)
                          )}
                        />
                      )}
                    </AnimatedCell>
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
          {displayMode === 2 && (
            <div
              style={{
                width: "100%",
                marginTop: "0px",
                alignSelf: "flex-start",
              }}>
              <Table bordered className="main" onClick={toggleDisplayMode}>
                <tbody>
                  <tr style={styles.Cells}>
                    <td className="Cells" style={styles.column_1}>
                      Spreads
                    </td>
                    <td className="Cells" style={styles.Cells}></td>
                    <td className="Cells" style={styles.Cells}></td>
                  </tr>
                  <tr>
                    <td className="Cells" style={{ ...styles.column_1, width: "5%" }}></td>
                    <td className="Cells" style={{ ...styles.column_1, width: "25%", color: "#20B2AA" }}>
                      {game.home_team}
                    </td>
                    <td className="Cells" style={{ ...styles.column_1, width: "25%", color: "red" }}>
                      {game.away_team}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <HorizontalBarChart
                shortNames={["3G", "5G", "10G"]}
                rawData={[
                  diff(rateValue(game.head_to_head.h2h_away_spread_3, game.head_to_head.h2h_away_spread_sample_3, 3), rateValue(game.head_to_head.h2h_home_spread_3, game.head_to_head.h2h_home_spread_sample_3, 3)),
                  diff(rateValue(game.head_to_head.h2h_away_spread_5, game.head_to_head.h2h_away_spread_sample_5, 5), rateValue(game.head_to_head.h2h_home_spread_5, game.head_to_head.h2h_home_spread_sample_5, 5)),
                  diff(rateValue(game.head_to_head.h2h_away_spread_10, game.head_to_head.h2h_away_spread_sample_10, 10), rateValue(game.head_to_head.h2h_home_spread_10, game.head_to_head.h2h_home_spread_sample_10, 10)),
                ]}
                names={["Last 3", "Last 5", "Last 10"]}
                onClick={toggleDisplayMode}
              />
            </div>
          )}

          <LastTen
            results={Array(10)
              .fill()
              .map((_, i) => game.head_to_head[`h2h_${i + 1}_spread_covered`])
              .reverse()}
            title={game.home_team + " vs " + game.away_team + " spreads covered"}
            mode="h2h"
          />
          <div style={{ height: "5vh" }}></div>

          {
            <ul
              style={{
                listStyleType: "none",
                padding: 0,
                margin: 0,
                fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
              }}>
              <ReactMarkdown>{game.box_score && game.box_score["spreads_h2h"]}</ReactMarkdown>
            </ul>
          }
        </section>

        <section style={sectionSnap}>
          <Table bordered className="main" onClick={togglePointDifference}>
            <tbody>
              <tr style={styles.Cells}>
                <td className="Cells" style={styles.column_1}></td>
                <td className="Cells" style={styles.column_1}>
                  Point Totals
                </td>
                <td className="Cells" style={styles.column_1}></td>
                <td className="Cells" style={styles.column_1}></td>
                <td className="Cells" style={styles.column_1}></td>
                <td className="Cells" style={styles.column_1}></td>
                <td className="Cells" style={styles.column_1}></td>
              </tr>

              <tr>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  {game.home_team}{" "}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  {game.away_team}{" "}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Overs
                </td>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Total
                </td>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Delta{" "}
                </td>
              </tr>
              <tr style={styles.Cells}>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Last 3
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_home_PF_3, game.head_to_head.h2h_home_PA_3), 1, { signed: true })
                      : pair(game.head_to_head.h2h_home_PF_3, game.head_to_head.h2h_home_PA_3)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_away_PF_3, game.head_to_head.h2h_away_PA_3), 1, { signed: true })
                      : pair(game.head_to_head.h2h_away_PF_3, game.head_to_head.h2h_away_PA_3)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? rate(game.head_to_head.h2h_overs_hit_3, game.head_to_head.h2h_overs_sample_3, 3)
                      : record(game.head_to_head.h2h_overs_hit_3, game.head_to_head.h2h_overs_sample_3, 3)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(mean(sum(game.head_to_head.h2h_home_PF_3, game.head_to_head.h2h_home_PA_3), sum(game.head_to_head.h2h_away_PF_3, game.head_to_head.h2h_away_PA_3)))}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(diff(mean(sum(game.head_to_head.h2h_home_PF_3, game.head_to_head.h2h_home_PA_3), sum(game.head_to_head.h2h_away_PF_3, game.head_to_head.h2h_away_PA_3)), game.over), 1, { signed: true })}
                </td>
              </tr>
              <tr style={styles.Cells}>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Last 5
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_home_PF_5, game.head_to_head.h2h_home_PA_5), 1, { signed: true })
                      : pair(game.head_to_head.h2h_home_PF_5, game.head_to_head.h2h_home_PA_5)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_away_PF_5, game.head_to_head.h2h_away_PA_5), 1, { signed: true })
                      : pair(game.head_to_head.h2h_away_PF_5, game.head_to_head.h2h_away_PA_5)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? rate(game.head_to_head.h2h_overs_hit_5, game.head_to_head.h2h_overs_sample_5, 5)
                      : record(game.head_to_head.h2h_overs_hit_5, game.head_to_head.h2h_overs_sample_5, 5)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(mean(sum(game.head_to_head.h2h_home_PF_5, game.head_to_head.h2h_home_PA_5), sum(game.head_to_head.h2h_away_PF_5, game.head_to_head.h2h_away_PA_5)))}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(diff(mean(sum(game.head_to_head.h2h_home_PF_5, game.head_to_head.h2h_home_PA_5), sum(game.head_to_head.h2h_away_PF_5, game.head_to_head.h2h_away_PA_5)), game.over), 1, { signed: true })}
                </td>
              </tr>
              <tr style={styles.Cells}>
                <td className="Cells" style={styles.Cells}>
                  {" "}
                  Last 10
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_home_PF_10, game.head_to_head.h2h_home_PA_10), 1, { signed: true })
                      : pair(game.head_to_head.h2h_home_PF_10, game.head_to_head.h2h_home_PA_10)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? fmt(diff(game.head_to_head.h2h_away_PF_10, game.head_to_head.h2h_away_PA_10), 1, { signed: true })
                      : pair(game.head_to_head.h2h_away_PF_10, game.head_to_head.h2h_away_PA_10)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  <AnimatedCell>
                    {showPointDifference
                      ? rate(game.head_to_head.h2h_overs_hit_10, game.head_to_head.h2h_overs_sample_10, 10)
                      : record(game.head_to_head.h2h_overs_hit_10, game.head_to_head.h2h_overs_sample_10, 10)}
                  </AnimatedCell>
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(mean(sum(game.head_to_head.h2h_home_PF_10, game.head_to_head.h2h_home_PA_10), sum(game.head_to_head.h2h_away_PF_10, game.head_to_head.h2h_away_PA_10)))}
                </td>
                <td className="Cells" style={styles.Cells}>
                  {fmt(diff(mean(sum(game.head_to_head.h2h_home_PF_10, game.head_to_head.h2h_home_PA_10), sum(game.head_to_head.h2h_away_PF_10, game.head_to_head.h2h_away_PA_10)), game.over), 1, { signed: true })}
                </td>
              </tr>
            </tbody>
          </Table>

          <LastTen
            results={Array(10)
              .fill()
              .map((_, i) => game.head_to_head[`h2h_${i + 1}_over_hit`])
              .reverse()}
            title={game.home_team + " vs " + game.away_team + " overs hit"}
          />

          <div style={{ height: "5vh" }}></div>

          <Chart fixture={game} mode="h2h" />

          <div style={{ height: "10vh" }}></div>

          {
            <ul
              style={{
                listStyleType: "none",
                padding: 0,
                margin: 0,
                fontSize: "clamp(10px, calc(7px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
              }}>
              <ReactMarkdown>{game.box_score && game.box_score["totals_h2h"]}</ReactMarkdown>
            </ul>
          }
        </section>
        <section style={sectionSnap}>
          <Card
            className="cardbutton"
            style={{
              ...styles.cardbutton,
              width: "100%",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (!isExpanded) {
                fetchData();
              }
            }}>
            <Card.Header>
              <h5 style={{ margin: 0 }}>
                {team1} vs {team2} Head to Head Matchups
              </h5>
            </Card.Header>
          </Card>

          {isExpanded && (
            <motion.div
              key={isLoading ? "loading" : "content"}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ overflow: "hidden" }}>
              {isLoading ? (
                <div
                  className="d-flex justify-content-center"
                  style={{
                    marginTop: "20px",
                    marginLeft: "40px",
                    marginRight: "90px",
                    marginBottom: "40px",
                    height: "100%",
                    width: "100%",
                  }}>
                  <Oval strokeWidth={5} height={200} width={200} />
                </div>
              ) : (
                <div style={{ marginTop: "20px" }}>
                  {data.map((fixture) => (
                    <div key={fixture.id}>
                      <Winner fixture={fixture} team1={team1} team2={team2} />
                      <OddsTable fixture={fixture} mode="HeadtoHead" />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </section>
        {onClose && !isDesktopMode && (
          <section style={sectionSnap}>
            <Card className="cardbutton" style={{ ...styles.cardbutton, width: "100%" }} onClick={onClose}>
              <Card.Text>Close Head to Head</Card.Text>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
}

export default HeadtoHead;
